import { useCallback, useEffect, useState } from 'react';
import { useSearchParam } from '@client/hooks/use-search-param.hook';
import {
  getSentryTestErrorMessage,
  SENTRY_TEST_MESSAGE_PARAM,
} from '@shared/helpers/sentry-test.helpers';
import { adminSentryTestApi } from './admin-sentry-test.api';
import { isSentryTestServerThrow } from './admin-sentry-test.helpers';

export type SentryTestServerState =
  'idle' | 'thrown' | 'failed' | 'unexpected-ok';

type SentryTestRun = {
  id: number;
  raw: string;
};

type UseSentryTestOptions = {
  /** When false (not admin), the page must not throw or call the API. */
  enabled: boolean;
};

type StartSentryTestRun = (raw: string) => void;

/**
 * Reads `?message=` as the test text. A run always fires the API and client
 * throw, including when the URL param is unchanged (Throw must re-fire).
 */
export const useSentryTest = ({ enabled }: UseSentryTestOptions) => {
  const [rawMessage, setRawMessage] = useSearchParam(SENTRY_TEST_MESSAGE_PARAM);
  const [run, setRun] = useState<SentryTestRun>();
  const [serverState, setServerState] = useState<SentryTestServerState>('idle');

  const startRun = useCallback<StartSentryTestRun>((raw) => {
    setRun((prev) => ({ id: (prev?.id ?? 0) + 1, raw }));
  }, []);

  const urlErrorMessage = getSentryTestErrorMessage(rawMessage);
  // First visit with ?message= starts one run; later Throw calls startRun.
  if (enabled && urlErrorMessage && run === undefined) {
    setRun({ id: 1, raw: rawMessage });
  }

  const errorMessage = getSentryTestErrorMessage(run?.raw);
  const shouldThrowClient = Boolean(enabled && errorMessage && run);

  useEffect(() => {
    if (!enabled || !run || !getSentryTestErrorMessage(run.raw)) {
      setServerState('idle');
      return;
    }

    let cancelled = false;
    void adminSentryTestApi.throwError(run.raw).then(
      () => {
        if (!cancelled) setServerState('unexpected-ok');
      },
      (error: unknown) => {
        if (cancelled) return;
        setServerState(isSentryTestServerThrow(error) ? 'thrown' : 'failed');
      },
    );
    return () => {
      cancelled = true;
    };
  }, [enabled, run]);

  return {
    rawMessage,
    setRawMessage,
    errorMessage,
    serverState,
    shouldThrowClient,
    startRun,
    runId: run?.id ?? 0,
  };
};
